import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { shallowEqual, useSelector } from 'react-redux';
import { selectedProfileSelector } from '../../redux/common/search-data/selectors';
import { updateFormStatusSelector } from '../../redux/common/formValueChange/selectors';
import { buildHostMessage, HOST_MESSAGES } from './messages';
import { buildPageContext, buildUserFromLocalStorage } from './pageContext';

const PUBLISH_DEBOUNCE_MS = 300;

/**
 * Publishes host:context into the Aristotle iframe: once on the iframe's ready
 * handshake (call the returned publishNow) and again — debounced, deduplicated —
 * whenever the SIS route or the selected person changes. Publishing continues
 * while the panel is collapsed, so reopening it already has the current page.
 */
export const usePageContextPublisher = ({ iframeRef, targetOrigin, product, enabled }) => {
    const location = useLocation();
    const selectedProfile = useSelector(selectedProfileSelector, shallowEqual);
    const { sideBarItems } = useSelector(updateFormStatusSelector, shallowEqual);
    const lastSentRef = useRef('');

    const publishNow = useCallback(() => {
        if (!enabled || !targetOrigin) return;
        const frameWindow = iframeRef.current?.contentWindow;
        if (!frameWindow) return;
        const context = buildPageContext({
            href: window.location.href,
            pathname: location.pathname,
            sideBarItems,
            selectedProfile,
            user: buildUserFromLocalStorage(),
            product,
            tenantHost: window.location.hostname,
        });
        const key = JSON.stringify(context);
        if (key === lastSentRef.current) return;
        lastSentRef.current = key;
        frameWindow.postMessage(buildHostMessage(HOST_MESSAGES.CONTEXT, context), targetOrigin);
    }, [enabled, targetOrigin, iframeRef, location.pathname, selectedProfile, sideBarItems, product]);

    // Route/profile changes republish automatically (debounced so rapid
    // navigation + profile dispatches collapse into one message).
    useEffect(() => {
        if (!enabled || !targetOrigin) return undefined;
        const timer = setTimeout(publishNow, PUBLISH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [enabled, targetOrigin, publishNow]);

    return { publishNow };
};
